import { FC } from 'react';
import classNames from 'classnames';

import { TEXTS } from 'application/pages/BillingPage/renderer/texts';
import CommonUtils from 'application/utils/CommonUtils';

import Text from '@zvuk-b2b/react-uikit/ui/Text';
import IconCoinsAlt from '@zvuk-b2b/react-uikit/ui/icons/CoinsAlt';
import Info, { InfoTypes } from '@zvuk-b2b/react-uikit/ui/Info';
import IconInfo from '@zvuk-b2b/react-uikit/ui/icons/Info';
import Popper, { HintPlacement } from '@zvuk-b2b/react-uikit/ui/Popper';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import { AmountProps } from './types';
import useAmountViewModel from './useAmountViewModel';

import styles from './index.module.scss';

const Amount: FC<AmountProps> = ({
  selectedPriceItemPeriodId,
  periodPayFrequencyMonth,
  currentPeriodId,
  withPricePlanAlias,
}) => {
  const { isDesktop } = useMedia();

  const viewModel = useAmountViewModel({
    selectedPriceItemPeriodId,
    currentPeriodId,
    withPricePlanAlias,
    periodPayFrequencyMonth,
  });

  if (!viewModel) {
    return (
      <Text
        elementId="title_subscription_cost_by_price_period_id"
        className={styles.amountSkeleton}
        isLoading
        as="div"
      />
    );
  }

  const {
    subscriptionCostByPricePlanPeriod,
    nextBillingAmount,
    fullPeriodAmount,
    fullPeriodAmountWithDiscount,
    licenseCostLoading,
    withDiscountAndCurrentSelected,
    withInactiveLicensesInfo,
    withResidualPayment,
    formatNextBillingDate,
    formatNextBillingDateShort,
  } = viewModel;

  return (
    <div
      className={classNames(styles.container, {
        [styles.containerVertical]: withInactiveLicensesInfo,
      })}>
      <div className={styles.amountTitle}>
        <IconCoinsAlt className={styles.icon} />
        <span>{TEXTS.PRICE_PLAN_LIST_ITEM_RATE_TEXT(withPricePlanAlias)}</span>
      </div>

      {withInactiveLicensesInfo ? (
        <>
          <Info
            type={InfoTypes.ERROR}
            className={styles.info}>
            {TEXTS.RATES_BLOCK_INACTIVE_SUBSCRIPTION_INFO}
          </Info>
        </>
      ) : (
        <>
          <div className={styles.amountWithPeriod}>
            <Text
              elementId="title_subscription_cost_by_price_period_id"
              className={licenseCostLoading ? styles.amountSkeleton : styles.amount}
              isLoading={licenseCostLoading}
              as="div">
              {TEXTS.BILLING_LICENSES_AMOUNT(
                CommonUtils.numberWithSeparation(
                  withDiscountAndCurrentSelected || withResidualPayment
                    ? nextBillingAmount
                    : subscriptionCostByPricePlanPeriod
                )
              )}
            </Text>

            {withResidualPayment ? (
              <div className={styles.periodFull}>
                <div>{isDesktop ? formatNextBillingDate : formatNextBillingDateShort}</div>

                <Popper
                  placement={HintPlacement.PLACEMENT_TOP}
                  isShow
                  triggerNode={
                    <div>
                      <IconInfo className={styles.icon} />
                    </div>
                  }
                  popperClassName={styles.popper}>
                  {TEXTS.BILLING_CALENDAR_MONTHLY_PAYMENT_SCHEME(
                    withDiscountAndCurrentSelected ? fullPeriodAmountWithDiscount : fullPeriodAmount
                  )}
                </Popper>
              </div>
            ) : (
              <div className={styles.period}>
                {periodPayFrequencyMonth === 1 ? TEXTS.RATES_BLOCK_RATES_MONTH_TEXT : TEXTS.RATES_BLOCK_RATES_YEAR_TEXT}
              </div>
            )}
          </div>

          {withResidualPayment && (
            <Text
              elementId="full_cost_for_calendar_monthly_payment_scheme"
              className={licenseCostLoading ? styles.amountSkeletonSmall : styles.fullAmountContainer}
              isLoading={licenseCostLoading}
              as="div">
              <div>{TEXTS.BILLING_LICENSES_FULL_AMOUNT_TEXT}</div>

              <div className={styles.fullAmount}>
                {TEXTS.BILLING_LICENSES_AMOUNT(
                  CommonUtils.numberWithSeparation(
                    withDiscountAndCurrentSelected ? fullPeriodAmountWithDiscount : fullPeriodAmount
                  )
                )}
              </div>

              <div className={styles.period}>
                {periodPayFrequencyMonth === 1 ? TEXTS.RATES_BLOCK_RATES_MONTH_TEXT : TEXTS.RATES_BLOCK_RATES_YEAR_TEXT}
              </div>
            </Text>
          )}
        </>
      )}
    </div>
  );
};

export default Amount;
